'use client';
import React, { CSSProperties, useMemo } from 'react';
import Slider from 'react-slick';
import { Card } from '@components/card';
import { cn } from '@lib/utils';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import './style.css';
import { StaticImageData } from 'next/image';
import { TrackedEventOptions } from '@/constants';
import { useTranslations } from 'next-intl';
import { useLastCardVisibility } from './use-last-card-visibility';

interface CardProps {
  id: string;
  category: React.ReactNode;
  title: React.ReactNode;
  date: string;
  src?: StaticImageData | string;
  href: string;
  color?: string;
}

interface CustomCardProps {
  customContent: React.ReactNode;
}

const isCustomCard = (
  card: CardProps | CustomCardProps,
): card is CustomCardProps =>
  (card as CustomCardProps).customContent !== undefined;

interface SlickArrowProps {
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

function SlickArrow({
  ariaLabel,
  disabled,
  className,
  style,
  onClick,
}: SlickArrowProps) {
  return (
    <button
      type="button"
      className={cn(className, disabled && 'slick-disabled')}
      style={style}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    />
  );
}

interface CardCarouselProps {
  data: (CardProps | CustomCardProps)[];
  maxWidth?: string;
  className?: string;
  cardWidth?: string;
  cardHeight?: string;
  marginLeft?: string;
  options?: TrackedEventOptions;
}

function CardCarousel({
  data,
  maxWidth = '1420px',
  className,
  cardWidth = '370px',
  cardHeight = '470px',
  marginLeft = 'ml-[max(calc((100vw-var(--max-width))/2),20px)]',
  options,
}: CardCarouselProps) {
  const { lastCardRef, isLastCardVisible } = useLastCardVisibility(data.length);
  const t = useTranslations('Common.carousel');

  const prevArrow = useMemo(
    () => <SlickArrow ariaLabel={t('previousSlide')} />,
    [t],
  );
  const nextArrow = useMemo(
    () => (
      <SlickArrow ariaLabel={t('nextSlide')} disabled={isLastCardVisible} />
    ),
    [t, isLastCardVisible],
  );

  const settings = {
    dots: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    className,
    prevArrow,
    nextArrow,
  };

  return (
    <div
      style={
        {
          '--max-width': maxWidth,
          '--card-width': cardWidth,
          '--card-height': cardHeight,
        } as CSSProperties
      }
    >
      <Slider {...settings}>
        {data.map((card, index) => {
          const isLastCard = index === data.length - 1;

          if (isCustomCard(card)) {
            return (
              <div key={index} ref={isLastCard ? lastCardRef : undefined}>
                {card.customContent}
              </div>
            );
          }

          const { category, date, title, src, href, color } = card;
          const gtagOptions = options?.gtagOptions?.addGtag
            ? {
                ...options?.gtagOptions,
                label: `${options?.gtagOptions?.label}-${card.id}`,
              }
            : undefined;

          return (
            <div key={href} ref={isLastCard ? lastCardRef : undefined}>
              <Card
                options={{
                  ...options,
                  gtagOptions,
                }}
                href={href}
                src={src}
                className={cn(
                  'mx-[12px] h-[406px] w-[320px] md:h-[var(--card-height)] md:w-[var(--card-width)]',
                  index === 0 ? marginLeft : '',
                  color,
                )}
              >
                <Card.Info className="mb-[8px]">
                  <span>{category}</span> | {date}
                </Card.Info>
                <Card.Title>{title}</Card.Title>
              </Card>
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

export type { CardProps, CustomCardProps };
export default CardCarousel;
